import React from 'react';
import { IonButton, IonContent, IonSkeletonText } from '@ionic/react';
import type { ActionGuardLock } from '@/components/ActionGuard';
import styles from './RecipientsSkeleton.module.css';

// Used only if a lock's help text carries no [bracketed] label of its own; every
// string that reaches this page does.
const FALLBACK_BUTTON_LABEL = 'Continue';

// Name and detail line widths for the placeholder contact rows, varied so the
// rows read as a list of different people rather than one repeated row.
const ROW_WIDTHS: ReadonlyArray<[name: string, detail: string]> = [
  ['45%', '30%'],
  ['60%', '25%'],
  ['38%', '35%'],
  ['52%', '20%'],
  ['42%', '32%'],
  ['56%', '28%'],
  ['35%', '24%'],
];

/**
 * What the recipients step shows a sender who can't share yet, in place of the
 * picker: a sketch of the picker's layout — the recipient box, the
 * exclude-aware checkbox and a list of contacts — with the lock's own
 * explanation and its button where the picker's advance button sits. Rendered
 * as the `fallbackElem` of the step's `ActionGuard`, which hands over the
 * resolved `lock`, so the copy and destination follow whatever blocks the
 * share: the Familiar Name the profile still lacks, or a verification.
 *
 * Nothing on it can be picked, so a sender who comes back from the profile
 * without a name can't start choosing people for a share they can't send.
 */
const RecipientsSkeleton: React.FC<{ lock: ActionGuardLock }> = ({ lock }) => (
  <IonContent className={styles.content} scrollY={false}>
    <div className={styles.body} data-testid="recipients-skeleton">
      <div className={styles.top}>
        <IonSkeletonText animated className={styles.recipientBox} />
        <div className={styles.checkboxRow}>
          <IonSkeletonText animated className={styles.checkbox} />
          <IonSkeletonText animated className={styles.checkboxLabel} />
        </div>
      </div>
      <div className={styles.rows}>
        {ROW_WIDTHS.map(([nameWidth, detailWidth], i) => (
          <div key={i} className={styles.row}>
            <IonSkeletonText animated className={styles.avatar} />
            <div className={styles.rowText}>
              <IonSkeletonText
                animated
                className={styles.line}
                style={{ '--line-width': nameWidth } as React.CSSProperties}
              />
              <IonSkeletonText
                animated
                className={styles.line}
                style={{ '--line-width': detailWidth } as React.CSSProperties}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.lock}>
        <p
          className={styles.helpText}
          data-testid="recipients-skeleton-help-text"
        >
          {lock.helpText}
        </p>
        {lock.isUnlockable && (
          <IonButton
            expand="block"
            color="primary"
            className={styles.unlockButton}
            onClick={lock.navigateToUnlock}
            data-testid="recipients-skeleton-unlock-button"
          >
            {lock.buttonText ?? FALLBACK_BUTTON_LABEL}
          </IonButton>
        )}
      </div>
    </div>
  </IonContent>
);

export default RecipientsSkeleton;
